/**
 * FRONT-14: Onglet Références - Refonte complète
 * 
 * Améliorations vs FRONT-12:
 * - Mode lecture/édition (comme TabPresentation)
 * - Upload sans rechargement complet (mise à jour locale)
 * - Validation fichiers renforcée (images uniquement, pas de PDF)
 * - Pastilles de statut (complète/obligatoire)
 * - Design System Centralis (tailles de police corrigées)
 * - Message "3 références obligatoires"
 */
import React, { useState, useEffect, useCallback } from 'react';
import Button from '../../../composants/Button/Button';
import * as profileService from '../../../services/profileService';
import type { Reference, ReferenceData } from '../../../services/profileService';
import { useProfile } from '../../../contexts/ProfileContext';
import './TabReferences.css';

// ============================================
// Constantes de validation
// ============================================
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png'];
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB


// ============================================
// Composant principal
// ============================================
const TabReferences: React.FC = () => {
  const { refreshCompletion } = useProfile();
  const [references, setReferences] = useState<Reference[]>([]);
  const [originalReferences, setOriginalReferences] = useState<Reference[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Charger les références
  const loadReferences = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await profileService.getReferences();
      setReferences(data.references);
      setOriginalReferences(data.references);
    } catch (err) {
      setError('Erreur lors du chargement des références');
      console.error('Erreur chargement références:', err);
      // Initialiser avec 3 références vides en cas d'erreur
      const emptyRefs: Reference[] = [
        { numero: 1, nom_operation: null, client: null, budget: null, annee_livraison: null, descriptif: null, has_photo: false, photo_filename: null, is_complete: false },
        { numero: 2, nom_operation: null, client: null, budget: null, annee_livraison: null, descriptif: null, has_photo: false, photo_filename: null, is_complete: false },
        { numero: 3, nom_operation: null, client: null, budget: null, annee_livraison: null, descriptif: null, has_photo: false, photo_filename: null, is_complete: false },
      ];
      setReferences(emptyRefs);
      setOriginalReferences(emptyRefs);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReferences();
  }, [loadReferences]);

  // Mise à jour d'un champ (mode édition)
  const handleFieldChange = (numero: number, field: keyof ReferenceData, value: string | number | null) => {
    setReferences(prev => prev.map(ref =>
      ref.numero === numero ? { ...ref, [field]: value } : ref
    ));
  };

  // Passer en mode édition
  const handleStartEdit = () => {
    setIsEditing(true);
  };

  // Annuler les modifications
  const handleCancel = () => {
    setReferences([...originalReferences]);
    setIsEditing(false);
    setError(null);
    setShowValidationErrors(false);
  };

  // Validation des données avant sauvegarde
  const validateReferences = (): boolean => {
    const currentYear = new Date().getFullYear();
    for (const ref of references) {
      if (!ref.nom_operation || !ref.client || !ref.budget || !ref.annee_livraison || !ref.descriptif || !ref.has_photo)
        return false;
      if (ref.annee_livraison < 1900 || ref.annee_livraison > currentYear + 1)
        return false;
    }
    return true;
  };

  // Sauvegarde
  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    // Validation locale avant envoi
    if (!validateReferences()) {
      setShowValidationErrors(true);
      setIsSaving(false);
      setTimeout(() => {
        document.querySelector('.reference-card.edit-mode .input-error, .photo-dropzone--error')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }
    setShowValidationErrors(false);

    try {
      const dataToSend: ReferenceData[] = references.map(ref => ({
        numero: ref.numero,
        nom_operation: ref.nom_operation,
        client: ref.client,
        budget: ref.budget,
        annee_livraison: ref.annee_livraison,
        descriptif: ref.descriptif,
      }));

      const result = await profileService.updateReferences(dataToSend);
      setReferences(result.references);
      setOriginalReferences(result.references);
      setIsEditing(false);
      setShowValidationErrors(false);
      setSuccessMessage('Références enregistrées avec succès');
      setTimeout(() => setSuccessMessage(null), 5000);
      // Rafraîchir la complétion (pastilles onglets + sidebar)
      // await pour que setCompletion soit appelé avant la fin du try
      await refreshCompletion();
    } catch (err: any) {
      // Gérer les erreurs de validation de l'API
      if (err?.errors) {
        const apiErrors = Object.values(err.errors).flat().join(' | ');
        setError(apiErrors);
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError('Erreur lors de la sauvegarde des références');
      }
      console.error('Erreur sauvegarde références:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // FRONT-14: Upload photo SANS rechargement complet
  const handlePhotoUpload = async (numero: number, file: File) => {
    setError(null);
    try {
      const result = await profileService.uploadReferencePhoto(numero, file);

      // Mise à jour LOCALE uniquement (pas de reload)
      setReferences(prev => prev.map(ref =>
        ref.numero === numero
          ? { ...ref, has_photo: true, photo_filename: result.data?.filename || file.name }
          : ref
      ));

      // Mettre à jour aussi originalReferences pour les photos (déjà sauvées côté serveur)
      setOriginalReferences(prev => prev.map(ref =>
        ref.numero === numero
          ? { ...ref, has_photo: true, photo_filename: result.data?.filename || file.name }
          : ref
      ));

      setSuccessMessage('Photo uploadée avec succès');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Erreur lors de l\'upload de la photo');
      console.error('Erreur upload photo:', err);
    }
  };

  // Supprimer photo
  const handlePhotoDelete = async (numero: number) => {
    setError(null);
    try {
      await profileService.deleteReferencePhoto(numero);

      // Mise à jour locale
      setReferences(prev => prev.map(ref =>
        ref.numero === numero
          ? { ...ref, has_photo: false, photo_filename: null }
          : ref
      ));
      setOriginalReferences(prev => prev.map(ref =>
        ref.numero === numero
          ? { ...ref, has_photo: false, photo_filename: null }
          : ref
      ));

      setSuccessMessage('Photo supprimée');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Erreur lors de la suppression de la photo');
      console.error('Erreur suppression photo:', err);
    }
  };

  // Vérifier si une référence est complète localement (TOUS les champs obligatoires, y compris photo)
  const isReferenceComplete = (ref: Reference): boolean => {
    return !!(ref.nom_operation && ref.client && ref.budget && ref.annee_livraison && ref.descriptif && ref.has_photo);
  };

  if (isLoading) {
    return (
      <div className="tab-references loading">
        <div className="loading-spinner"></div>
        <p>Chargement des références...</p>
      </div>
    );
  }

  // ========================================
  // MODE LECTURE (par défaut)
  // ========================================
  if (!isEditing) {
    return (
      <div className="tab-references">
        <div className="tab-header">
          <h2>Principales références des trois dernières années</h2>
          <Button
            value="Modifier"
            variant="secondary"
            onClick={handleStartEdit}
          />
        </div>

        {/* FRONT-14: Message 3 références obligatoires */}
        <div className="info-banner info-banner-warning">
          <span className="info-icon">⚠️</span>
          <span>Les 3 références sont obligatoires pour compléter votre profil. Tous les champs (y compris la photo) doivent être renseignés.</span>
        </div>

        {error && (
          <div className="alert alert-error">
            <span>{error}</span>
            <button className="alert-close" onClick={() => setError(null)}>×</button>
          </div>
        )}
        {successMessage && (
          <div className="alert alert-success">
            <span>{successMessage}</span>
            <button className="alert-close" onClick={() => setSuccessMessage(null)}>×</button>
          </div>
        )}

        <div className="references-list">
          {references.map((ref) => (
            <ReferenceViewCard
              key={ref.numero}
              reference={ref}
              isComplete={ref.is_complete ?? isReferenceComplete(ref)}
            />
          ))}
        </div>
      </div>
    );
  }

  // ========================================
  // MODE ÉDITION
  // ========================================
  return (
    <div className="tab-references editing">
      <div className="tab-header">
        <h2>Principales références des trois dernières années</h2>
      </div>

      {/* FRONT-14: Message 3 références obligatoires
      <div className="info-banner info-banner-warning">
        <span className="info-icon">⚠️</span>
        <span>Les 3 références sont obligatoires pour compléter votre profil. Tous les champs (y compris la photo) doivent être renseignés.</span>
      </div> */}

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button className="alert-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {showValidationErrors && (() => {
        const n = references.filter(r => !isReferenceComplete(r)).length;
        return n > 0 ? (
          <div className="alert alert-validation-hint">
            <span className="alert-hint-icon">⚠</span>
            <span>
              {n === 3 ? 'Les 3 références sont' : `${n} référence${n > 1 ? 's' : ''} est`} incomplète{n > 1 ? 's' : ''} — Tous les champs (y compris la photo) doivent être renseignés..
            </span>
            <button className="alert-close" onClick={() => setShowValidationErrors(false)}>×</button>
          </div>
        ) : null;
      })()}

      {successMessage && (
        <div className="alert alert-success">
          <span>{successMessage}</span>
          <button className="alert-close" onClick={() => setSuccessMessage(null)}>×</button>
        </div>
      )}

      <div className="references-list">
        {references.map((ref) => (
          <ReferenceEditCard
            key={ref.numero}
            reference={ref}
            isComplete={isReferenceComplete(ref)}
            showErrors={showValidationErrors}
            onFieldChange={handleFieldChange}
            onPhotoUpload={handlePhotoUpload}
            onPhotoDelete={handlePhotoDelete}
          />
        ))}
      </div>

      <div className="footer-actions">
        <Button
          value="Annuler"
          variant="secondary"
          onClick={handleCancel}
          disabled={isSaving}
        />
        <Button
          value={isSaving ? "Enregistrement..." : "Enregistrer"}
          variant="primary"
          onClick={handleSave}
          disabled={isSaving}
        />
      </div>
    </div>
  );
};

// ============================================
// Composant carte référence - MODE LECTURE
// ============================================
interface ReferenceViewCardProps {
  reference: Reference;
  isComplete: boolean;
}

const ReferenceViewCard: React.FC<ReferenceViewCardProps> = ({ reference, isComplete }) => {
  const isEmpty = !reference.nom_operation && !reference.client && !reference.annee_livraison;
  const [isDownloading, setIsDownloading] = useState(false);

  // Télécharger la photo (avec authentification via fetch)
  const handleDownloadPhoto = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await profileService.downloadReferencePhoto(
        reference.numero,
        reference.photo_filename || `reference-${reference.numero}.jpg`
      );
    } catch (err) {
      console.error('Erreur téléchargement photo:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="reference-card view-mode">
      <div className="section-banner">
        Référence {reference.numero}
        <ReferenceStatusBadge isComplete={isComplete} />
      </div>

      {isEmpty ? (
        <div className="section-content">
          <p className="empty-reference">Non renseignée</p>
        </div>
      ) : (
        <div className="section-content">
          <div className="view-row">
            <div className="view-item">
              <label>Nom de l'opération</label>
              <span>{reference.nom_operation || '-'}</span>
            </div>
            <div className="view-item">
              <label>Client</label>
              <span>{reference.client || '-'}</span>
            </div>
          </div>

          <div className="view-row">
            <div className="view-item">
              <label>Budget</label>
              <span>{reference.budget ? `${reference.budget.toLocaleString('fr-FR')} €` : '-'}</span>
            </div>
            <div className="view-item">
              <label>Année de livraison</label>
              <span>{reference.annee_livraison || '-'}</span>
            </div>
          </div>

          <div className="view-item full-width">
            <label>Descriptif</label>
            <p className="description-text">{reference.descriptif || '-'}</p>
          </div>

          {reference.has_photo && (
            <div className="view-item full-width">
              <label>Photo</label>
              <div
                className={`document-card clickable ${isDownloading ? 'downloading' : ''}`}
                onClick={handleDownloadPhoto}
              >
                <div className="document-content">
                  <span className="document-name">📎 {reference.photo_filename}</span>
                  <span className="document-hint">
                    {isDownloading ? 'Téléchargement...' : 'Cliquez pour télécharger'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// Composant carte référence - MODE ÉDITION
// ============================================
interface ReferenceEditCardProps {
  reference: Reference;
  isComplete: boolean;
  showErrors: boolean;
  onFieldChange: (numero: number, field: keyof ReferenceData, value: string | number | null) => void;
  onPhotoUpload: (numero: number, file: File) => void;
  onPhotoDelete: (numero: number) => void;
}

const ReferenceEditCard: React.FC<ReferenceEditCardProps> = ({
  reference,
  isComplete,
  showErrors,
  onFieldChange,
  onPhotoUpload,
  onPhotoDelete,
}) => {
  const currentYear = new Date().getFullYear();
  const maxYear = currentYear + 1;
  const minYear = 1900;
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [yearError, setYearError] = useState<string | null>(null);

  // Télécharger la photo (avec authentification)
  const handleDownloadPhoto = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await profileService.downloadReferencePhoto(
        reference.numero,
        reference.photo_filename || `reference-${reference.numero}.jpg`
      );
    } catch (err) {
      console.error('Erreur téléchargement photo:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  // FRONT-14: Validation renforcée des fichiers
  const validateFile = (file: File): { valid: boolean; error?: string } => {
    // Vérifier extension
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return { valid: false, error: 'Extension non autorisée. Utilisez : jpg, jpeg ou png' };
    }

    // Vérifier MIME type
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      return { valid: false, error: 'Type de fichier non autorisé. Seules les images sont acceptées.' };
    }

    // Vérifier taille
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: 'Fichier trop volumineux (max 10 MB)' };
    }

    return { valid: true };
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError(null);

    // FRONT-14: Validation renforcée
    const validation = validateFile(file);
    if (!validation.valid) {
      setUploadError(validation.error!);
      e.target.value = '';
      return;
    }

    setIsUploading(true);
    await onPhotoUpload(reference.numero, file);
    setIsUploading(false);

    // Reset input
    e.target.value = '';
  };

  const handleBudgetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onFieldChange(reference.numero, 'budget', value ? parseFloat(value) : null);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const year = value ? parseInt(value, 10) : null;

    // Validation de l'année
    if (year !== null) {
      if (year < minYear) {
        setYearError(`L'année doit être supérieure à ${minYear}`);
      } else if (year > maxYear) {
        setYearError(`L'année ne peut pas dépasser ${maxYear}`);
      } else {
        setYearError(null);
      }
    } else {
      setYearError(null);
    }

    onFieldChange(reference.numero, 'annee_livraison', year);
  };

  return (
    <div className="reference-card edit-mode">
      <div className="section-banner">
        Référence {reference.numero}
        <ReferenceStatusBadge isComplete={isComplete} />
      </div>

      <div className="section-content">
        <div className="form-row">
          <div className="form-group form-group-large">
            <label htmlFor={`nom_operation_${reference.numero}`} className="required">Nom de l'opération</label>
            <input
              id={`nom_operation_${reference.numero}`}
              type="text"
              value={reference.nom_operation || ''}
              onChange={(e) => onFieldChange(reference.numero, 'nom_operation', e.target.value || null)}
              placeholder="Ex: Construction pont de Normandie"
              maxLength={255}
              className={showErrors && !reference.nom_operation ? 'input-error' : ''}
              required
            />
            {showErrors && !reference.nom_operation && (
              <span className="field-error">Champ obligatoire</span>
            )}
          </div>

          <div className="form-group form-group-large">
            <label htmlFor={`client_${reference.numero}`} className="required">Client</label>
            <input
              id={`client_${reference.numero}`}
              type="text"
              value={reference.client || ''}
              onChange={(e) => onFieldChange(reference.numero, 'client', e.target.value || null)}
              placeholder="Ex: Département Seine-Maritime"
              maxLength={255}
              className={showErrors && !reference.client ? 'input-error' : ''}
              required
            />
            {showErrors && !reference.client && (
              <span className="field-error">Champ obligatoire</span>
            )}
          </div>

          <div className="form-group form-group-small">
            <label htmlFor={`budget_${reference.numero}`} className="required">Budget</label>
            <div className="input-with-suffix">
              <input
                id={`budget_${reference.numero}`}
                type="number"
                value={reference.budget ?? ''}
                onChange={handleBudgetChange}
                placeholder="0"
                min="0"
                step="0.01"
                className={showErrors && !reference.budget ? 'input-error' : ''}
                required
              />
              <span className="suffix">€</span>
            </div>
            {showErrors && !reference.budget && (
              <span className="field-error">Champ obligatoire</span>
            )}
          </div>

          <div className="form-group form-group-small">
            <label htmlFor={`annee_${reference.numero}`} className="required">Année</label>
            <input
              id={`annee_${reference.numero}`}
              type="number"
              value={reference.annee_livraison ?? ''}
              onChange={handleYearChange}
              placeholder="YYYY"
              min={minYear}
              max={maxYear}
              className={yearError || (showErrors && !reference.annee_livraison) ? 'input-error' : ''}
              required
            />
            {yearError && <span className="field-error">{yearError}</span>}
            {!yearError && showErrors && !reference.annee_livraison && (
              <span className="field-error">Champ obligatoire</span>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor={`descriptif_${reference.numero}`} className="required">Descriptif</label>
          <textarea
            id={`descriptif_${reference.numero}`}
            value={reference.descriptif || ''}
            onChange={(e) => onFieldChange(reference.numero, 'descriptif', e.target.value || null)}
            placeholder="Décrivez brièvement le projet, vos responsabilités, les résultats obtenus..."
            rows={4}
            maxLength={5000}
            className={showErrors && !reference.descriptif ? 'input-error' : ''}
            required
          />
          <div className="descriptif-footer">
            {showErrors && !reference.descriptif && (
              <span className="field-error">Champ obligatoire</span>
            )}
            <span className="char-count">{(reference.descriptif || '').length}/5000</span>
          </div>
        </div>

        <div className="form-group">
          <label className="required">Photo</label>

          {uploadError && (
            <div className="upload-error">
              <span>⚠️ {uploadError}</span>
              <button type="button" onClick={() => setUploadError(null)}>×</button>
            </div>
          )}

          {reference.has_photo ? (
            <div className="photo-preview">
              <div className="document-card">
                <div className="document-content">
                  <span className="document-name">📎 {reference.photo_filename}</span>
                </div>
              </div>
              <div className="photo-actions">
                <button
                  type="button"
                  className="btn-download"
                  onClick={handleDownloadPhoto}
                  disabled={isDownloading}
                >
                  {isDownloading ? 'Téléchargement...' : 'Télécharger'}
                </button>
                <button
                  type="button"
                  className="btn-delete"
                  onClick={() => onPhotoDelete(reference.numero)}
                >
                  Supprimer
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className={`photo-dropzone ${isUploading ? 'uploading' : ''} ${showErrors && !reference.has_photo ? 'photo-dropzone--error' : ''}`}>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  id={`photo-${reference.numero}`}
                  disabled={isUploading}
                />
                <label htmlFor={`photo-${reference.numero}`}>
                  {isUploading ? (
                    <>
                      <span className="upload-icon">⏳</span>
                      <span>Upload en cours...</span>
                    </>
                  ) : (
                    <>
                      <span className="upload-icon">⬆️</span>
                      <span>Cliquez pour sélectionner ou glissez-déposez un fichier</span>
                      <span className="upload-hint">Formats acceptés : .jpg, .jpeg, .png • Max 10MB</span>
                    </>
                  )}
                </label>
              </div>
              {showErrors && !reference.has_photo && (
                <span className="field-error field-error--photo">Une photo est obligatoire</span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// Composant pastille de statut
// ============================================
interface ReferenceStatusBadgeProps {
  isComplete: boolean;
}

const ReferenceStatusBadge: React.FC<ReferenceStatusBadgeProps> = ({ isComplete }) => {
  return (
    <span className={`status-badge ${isComplete ? 'complete' : 'incomplete'}`}>
      {isComplete ? '✓ Complète' : '⚠ Obligatoire'}
    </span>
  );
};

export default TabReferences;
